import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { UIToast, UIToastComponent, UIToastRef } from '@irohalab/deneb-ui';
import { catchError, EMPTY, map, Observable, Subject, switchMap, tap } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { navigateToUrl } from './utils';
import {
  FrontendKratos,
  LoginFlow,
  RecoveryFlow,
  RegistrationFlow,
  Session,
  SettingsFlow,
  UpdateLoginFlowBody,
  UpdateRecoveryFlowBody,
  UpdateRegistrationFlowBody,
  UpdateSettingsFlowBody,
  UpdateVerificationFlowBody,
  VerificationFlow
} from '../../sdk/kratos';
import { HttpErrorResponse } from '@angular/common/http';

export type FlowType = "login" | "registration" | "settings" | "recovery" | "verification";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private toastRef: UIToastRef<UIToastComponent>;

  authEvent = new Subject<any>();

  isSubmitting: boolean = false;

  constructor(private router: Router,
              private frontendKratos: FrontendKratos,
              @Inject(PLATFORM_ID) platformId: object,
              toast: UIToast) {
    this.toastRef = toast.makeText();
  }

  createOrGetRegistrationFlow(flowId?: string, afterVerificationReturnTo?: string, loginChallenge?: string): Observable<RegistrationFlow> {
    if (flowId) {
      return this.frontendKratos.getRegistrationFlow(flowId);
    } else {
      return this.frontendKratos.createBrowserRegistrationFlow(undefined, loginChallenge, afterVerificationReturnTo);
    }
  }

  updateRegistrationFlow(flowBody: UpdateRegistrationFlowBody, flowId: string, returnTo?: string): Observable<boolean> {
    return this.frontendKratos.updateRegistrationFlow(flowId,flowBody)
      .pipe(switchMap((data) => {
        // TODO: do some thing
        // console.log('This is the user session: ', data, data.identity);
        // continue_with is a list of actions that the user might need to take before the registration is complete.
        // It could, for example, contain a link to the verification form.
        if (data.continue_with) {
          for (const item of data.continue_with) {
            switch (item.action) {
              case 'show_verification_ui':
                const qp: any = {
                  flow: item.flow.id
                };
                if (returnTo) {
                  qp.return_to = returnTo;
                }
                return fromPromise(this.router.navigate(['verification'], {queryParams: qp}));
            }
          }
        }

        this.authEvent.next(data.identity);

        return fromPromise(navigateToUrl(this.router, returnTo || '/'));
      }));
  }

  createOrGetLoginFlow(flowId?: string, aal?: string, returnTo?: string, loginChallenge?: string): Observable<LoginFlow> {
    if (flowId) {
      return this.frontendKratos.getLoginFlow(flowId);
    } else {
      return this.frontendKratos.createBrowserLoginFlow(
        true,
        aal ?? undefined,
        returnTo ?? undefined,
        undefined,
        loginChallenge ?? undefined
      );
    }
  }

  updateLoginFlow(loginFlowBody: UpdateLoginFlowBody, flowId: string, returnTo?: string): Observable<boolean> {
    return this.frontendKratos.updateLoginFlow(String(flowId), loginFlowBody)
      .pipe(switchMap((data) => {
        this.authEvent.next(data.session.identity);
        // We logged in successfully! Let's bring the user home.
        if (returnTo) {
          return navigateToUrl(this.router, returnTo);
        } else {
          return navigateToUrl(this.router, '/');
        }
      }));
  }

  createOrGetVerificationFlow(flowId?: string, returnTo?: string): Observable<VerificationFlow> {
    let responseObservable: Observable<VerificationFlow>;
    if (flowId) {
      responseObservable = this.frontendKratos.getVerificationFlow(flowId);
    } else {
      responseObservable = this.frontendKratos.createBrowserVerificationFlow(returnTo);
    }
    return responseObservable.pipe(
      map((data) => data),
      catchError((err: any, caught: Observable<VerificationFlow>) => {
        switch (err.status) {
          case 410:
            // status code 410 means the request has expired - so let's load a fresh flow!
          case 403:
            // status code 403 implies some other issue (e.g. CSRF) - let's reload!
            if (flowId) {
              this.router.navigate(['verification']).then(() => {});
            }
            break;
          case 400:
            if (!flowId) {
              // Status code 400 implies the user is already signed in
              this.router.navigate(['']).then(() => {});
            }
            break;
        }
        throw err;
      })
    );
  }

  updateVerificationFlow(flowBody: UpdateVerificationFlowBody, flowId: string): Observable<VerificationFlow> {
    return this.frontendKratos.updateVerificationFlow(flowId, flowBody).pipe(map((data) => {
      this.isSubmitting = false;
      this.authEvent.next(null);
      return data;
    }));
  }

  createOrGetRecoveryFlow(flowId?: string, returnTo?: string): Observable<RecoveryFlow> {
    if (flowId) {
      return this.frontendKratos.getRecoveryFlow(flowId);
    } else {
      return this.frontendKratos.createBrowserRecoveryFlow(returnTo || "");
    }
  }

  updateRecoveryFlow(flowBody: UpdateRecoveryFlowBody, flowId: string): Observable<RecoveryFlow> {
    return this.frontendKratos.updateRecoveryFlow(flowId, flowBody)
      .pipe(tap((data) => {
        this.authEvent.next(null);
      }))
  }

  createOrGetSettingsFlow(flowId?: string, returnTo?: string): Observable<SettingsFlow> {
    if (flowId) {
      return this.frontendKratos.getSettingsFlow(flowId);
    } else {
      return this.frontendKratos.createBrowserSettingsFlow(String(returnTo || ''));
    }
  }

  updateSettingsFlow(flowBody: UpdateSettingsFlowBody, flowId: string): Observable<SettingsFlow> {
    return this.frontendKratos.updateSettingsFlow(flowId, flowBody)
      .pipe(tap(() => {
        this.authEvent.next(null);
      }));
  }

  createLogoutFlowToken(): Observable<string> {
    return this.frontendKratos.createBrowserLogoutFlow()
      .pipe(map((data) => data.logout_token), catchError((err: HttpErrorResponse) => {
        switch (err.status) {
          case 401:
            // do nothing, the user is not logged in
            return EMPTY;
        }

        // Something else happened
        throw err;
      }));
  }

  checkCurrentSession(): Observable<Session> {
    return this.frontendKratos.toSession();
  }

  logout(token?: string): Observable<boolean> {
    if (token) {
      return this.frontendKratos.updateLogoutFlow(token)
        .pipe(switchMap(() => {
          this.authEvent.next(null);
          return this.router.navigate(['login']);
        }));
    } else {
      return EMPTY;
    }
  }

  handleGetFlowError<S>(err: HttpErrorResponse , flowType: FlowType, resetFlow: () => void): Observable<any> {
    const data = err.error;
    switch (data?.error?.id) {
      case 'session_inactive':
        return fromPromise(this.router.navigate(['login'], {queryParams: {return_to: this.router.url}}));
      case 'session_aal2_required':
        if (data?.redirect_browser_to) {
          const redirectTo = new URL(data?.redirect_browser_to);
          if (flowType === 'settings') {
            redirectTo.searchParams.set('return_to', this.router.url);
          }
          // 2FA is enabled and enforced, but user did not perform 2fa yet!
          return fromPromise(navigateToUrl(this.router, redirectTo.toString()));
        }
        return fromPromise(this.router.navigate(['login'], {queryParams: {return_to: this.router.url}}));
      case 'session_already_available':
        // User is already signed in, let's redirect them home!
        return fromPromise(this.router.navigate([''], {replaceUrl: true}));
      case 'session_refresh_required':
        // We need to re-authenticate to perform this action
        return fromPromise(navigateToUrl(this.router, data?.redirect_browser_to));
      case 'self_service_flow_return_to_forbidden':
        // The flow expired, let's request a new one.
        this.toastRef.show("The return_to address is not allowed.");
        resetFlow();
        return fromPromise(this.router.navigate(this.flowTypeToRouteCommand(flowType)));
      case 'self_service_flow_expired':
        // The flow expired, let's request a new one. Kratos already created a fresh
        // flow and returns its id in `use_flow_id`. Reuse it so that the route keeps a
        // valid flow id instead of leaving the `flow` query param empty.
        this.toastRef.show('Your interaction expired, please fill out the form again.');
        resetFlow();
        return fromPromise(this.router.navigate(
          this.flowTypeToRouteCommand(flowType),
          data?.use_flow_id ? {queryParams: {flow: data.use_flow_id}} : undefined
        ));
      case 'security_csrf_violation':
        // A CSRF violation occurred. Best to just refresh the flow!
        this.toastRef.show(
          "A security violation was detected, please fill out the form again.",
        )
        resetFlow();
        return fromPromise(this.router.navigate(this.flowTypeToRouteCommand(flowType)));
      case 'security_identity_mismatch':
        // The requested item was intended for someone else. Let's request a new flow...
        resetFlow();
        return fromPromise(this.router.navigate(this.flowTypeToRouteCommand(flowType)));
      case 'browser_location_change_required':
        // Ory Kratos asked us to point the user to this URL.
        return fromPromise(navigateToUrl(this.router, data?.redirect_browser_to));
    }

    switch (err.status) {
      case 410:
        // The flow expired, let's request a new one.
        resetFlow();
        return fromPromise(this.router.navigate(this.flowTypeToRouteCommand(flowType)));
      case 400:
        if (data?.error?.code === 500) {
          // internal server error. probably bangumi-provider issue.
          this.toastRef.show(data.error.message || data.error.status || 'Internal Server Error');
          return EMPTY;
        }
    }

    // We are not able to handle the error? Return it.
    throw err;
  }

  private flowTypeToRouteCommand(flowType: FlowType): any[] {
    if (flowType === 'settings') {
      return ['settings', 'account'];
    } else {
      return [flowType];
    }
  }
}
